import Cookies from "js-cookie";
import { PROJECT_CODE } from "./company.js";
import { getCookieDomain, getEnv } from "./env.js";
export const THEME_KEY = "theme";
export const DEFAULT_THEME = "light";
/**
 * Get localStorage theme key based on deploy env
 * for vite env variables are prefixed with VITE_
 * for astro env variables are prefixed with PUBLIC_
 */
const getThemeKey = () => {
    return `${PROJECT_CODE}-${getEnv()}-${THEME_KEY}`;
};
/**
 * Get initial theme from localStorage.
 * Return null for non browser environment or if theme is not set
 */
const getCookieInitialTheme = () => {
    if (typeof window !== "undefined") {
        const themeKey = getThemeKey();
        const userTheme = Cookies.get(themeKey);
        if (userTheme === "dark") {
            return "dark";
        }
        if (userTheme === "light") {
            return "light";
        }
    }
    return null;
};
/**
 * Get initial theme from browser preferences.
 * Return null for non browser environment.
 */
const getBrowserInitialTheme = () => {
    if (typeof window === "undefined") {
        return null;
    }
    const { matches: isDarkMode, } = window.matchMedia("(prefers-color-scheme: dark)");
    return isDarkMode ? "dark" : "light";
};
/**
 * First check localStorage for theme
 * If defaultValue is auto, check browser preferences or return light
 * If defaultValue is dark or light, return defaultValue
 */
export const getInitialTheme = (defaultValue = "auto") => {
    const localStorageTheme = getCookieInitialTheme();
    if (localStorageTheme) {
        return localStorageTheme;
    }
    if (defaultValue === "light") {
        return "light";
    }
    if (defaultValue === "dark") {
        return "dark";
    }
    return getBrowserInitialTheme() ?? DEFAULT_THEME;
};
/**
 * Save initial theme to localStorage
 */
export const setInitialTheme = (theme) => {
    if (typeof window !== "undefined") {
        const themeKey = getThemeKey();
        Cookies.set(themeKey, theme, {
            expires: 365 * 10,
            domain: getCookieDomain(),
        });
    }
};
/**
 * Clear localStorage theme value
 */
export const clearColorScheme = () => {
    if (typeof window !== "undefined") {
        const themeKey = getThemeKey();
        Cookies.remove(themeKey, {
            domain: getCookieDomain(),
        });
    }
};
/**
 * LocalStorage theme manager for mantine provider
 * @see https://mantine.dev/theming/color-schemes/#color-scheme-manager
 */
export const localStorageColorSchemeManager = () => {
    let handleStorageEvent;
    const createHandleStorageEvent = (onUpdate) => {
        const themeKey = getThemeKey();
        return ({ changed }) => {
            for (const { value } of changed.filter(({ name }) => name === themeKey)) {
                if (value === "dark" || value === "light") {
                    const current = getInitialTheme();
                    if (value !== current) {
                        onUpdate(value);
                    }
                }
            }
        };
    };
    const subscribe = (onUpdate) => {
        handleStorageEvent = createHandleStorageEvent(onUpdate);
        if (typeof window !== "undefined") {
            cookieStore.addEventListener("change", handleStorageEvent);
        }
    };
    const unsubscribe = () => {
        if (typeof window !== "undefined") {
            cookieStore.removeEventListener("change", handleStorageEvent);
        }
    };
    return {
        get: getInitialTheme,
        set: setInitialTheme,
        clear: clearColorScheme,
        subscribe: subscribe,
        unsubscribe: unsubscribe,
    };
};
//# sourceMappingURL=theme.js.map